import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
    armWillChange,
    disarmWillChange,
    injectFoucStyles,
    isGsapDone,
    markGsapDone,
    markGsapReady,
    parseNumericDataAttr,
    prefersReducedMotion,
} from './_shared';

const SELECTOR = '[data-mda-gsap-stagger]';
const ITEM_SELECTOR = '[data-mda-gsap-stagger-item]';
const DEFAULT_GAP = 0.1;
const DEFAULT_Y = 32;
const DEFAULT_DURATION = 0.7;

function getStaggerItems(container) {
    const items = container.querySelectorAll(ITEM_SELECTOR);

    if (items.length) {
        return gsap.utils.toArray(items);
    }

    return gsap.utils.toArray(container.children);
}

function getStaggerOptions(container, options = {}) {
    return {
        gap: Number.isFinite(options.gap)
            ? options.gap
            : parseNumericDataAttr(container, 'mdaGsapStaggerGap', DEFAULT_GAP),
        y: Number.isFinite(options.y) ? options.y : DEFAULT_Y,
        duration: Number.isFinite(options.duration)
            ? options.duration
            : DEFAULT_DURATION,
    };
}

function prepareStagger(container, options = {}) {
    const items = getStaggerItems(container);

    if (!items.length) {
        return [];
    }

    const { y } = getStaggerOptions(container, options);

    gsap.set(items, {
        autoAlpha: 0,
        y,
    });
    markGsapReady(container);

    return items;
}

function runStagger(container, options = {}) {
    if (isGsapDone(container)) {
        return null;
    }

    const items = getStaggerItems(container);

    if (!items.length) {
        return null;
    }

    const { gap, duration } = getStaggerOptions(container, options);

    return gsap.to(items, {
        autoAlpha: 1,
        y: 0,
        duration,
        stagger: gap,
        ease: 'power2.out',
        overwrite: 'auto',
        clearProps: 'transform',
        onStart() {
            armWillChange(items);
        },
        onComplete() {
            disarmWillChange(items);
            markGsapDone(container);
        },
    });
}

export class Stagger {
    static selector = SELECTOR;

    constructor(element, options = {}) {
        if (!element) {
            throw new Error('Stagger requires an element.');
        }

        this.element = element;
        this.options = options;
        this.timeline = null;

        if (prefersReducedMotion()) {
            return;
        }

        this.create();
    }

    create() {
        const items = prepareStagger(this.element, this.options);

        if (!items.length) {
            return;
        }

        this.timeline = gsap.timeline({
            scrollTrigger: {
                trigger: this.element,
                start: 'top 80%',
                once: true,
                toggleActions: 'play play play none',
            },
        });

        const { gap, duration } = getStaggerOptions(this.element, this.options);
        const container = this.element;

        this.timeline.to(items, {
            autoAlpha: 1,
            y: 0,
            duration,
            stagger: gap,
            ease: 'power2.out',
            overwrite: 'auto',
            clearProps: 'transform',
            onStart() {
                armWillChange(items);
            },
            onComplete() {
                disarmWillChange(items);
                markGsapDone(container);
            },
        });
    }

    destroy() {
        this.timeline?.scrollTrigger?.kill();
        this.timeline?.kill();
        this.timeline = null;
    }

    static init(root = document, options = {}) {
        if (prefersReducedMotion() || !root?.querySelectorAll) {
            return [];
        }

        injectFoucStyles();

        const containers = Array.from(root.querySelectorAll(SELECTOR)).filter(
            element => getStaggerItems(element).length
        );

        if (!containers.length) {
            return [];
        }

        containers.forEach(container => {
            prepareStagger(container, options);
        });

        const handleStaggerBatch = batch => {
            batch.forEach(container => {
                runStagger(container, options);
            });
        };

        const batchTrigger = ScrollTrigger.batch(SELECTOR, {
            start: 'top 80%',
            once: true,
            fastScrollEnd: true,
            interval: 0.1,
            batchMax: 6,
            onEnter: handleStaggerBatch,
            onEnterBack: handleStaggerBatch,
        });

        return [
            {
                elements: containers,
                batchTrigger,
                destroy() {
                    (Array.isArray(batchTrigger)
                        ? batchTrigger
                        : [batchTrigger]
                    ).forEach(trigger => trigger?.kill?.());

                    gsap.killTweensOf(
                        containers.flatMap(container =>
                            getStaggerItems(container)
                        )
                    );
                },
            },
        ];
    }
}
